import { z } from 'zod';

export const INTEGRATION_PROVIDER_KEYS = {
  googleBusiness: 'google_business',
  uberEats: 'uber_eats',
  lieferando: 'lieferando',
} as const;

export type IntegrationProviderKey =
  (typeof INTEGRATION_PROVIDER_KEYS)[keyof typeof INTEGRATION_PROVIDER_KEYS];

export const IntegrationProviderSchema = z.enum([
  INTEGRATION_PROVIDER_KEYS.googleBusiness,
  INTEGRATION_PROVIDER_KEYS.uberEats,
  INTEGRATION_PROVIDER_KEYS.lieferando,
]);

/**
 * Bootstrap-only discovery adapter (Places API New). Not an OAuth connection row.
 * @see docs/architecture/channel-fabric.md
 */
export const CHANNEL_BOOTSTRAP_KEYS = {
  googlePlaces: 'google_places',
} as const;

export type ChannelBootstrapKey =
  (typeof CHANNEL_BOOTSTRAP_KEYS)[keyof typeof CHANNEL_BOOTSTRAP_KEYS];

/** Channel Fabric capability tokens — adapters declare what they support. */
export const CHANNEL_CAPABILITIES = {
  bootstrapRead: 'bootstrap_read',
  connectOauth: 'connect_oauth',
  identityPull: 'identity_pull',
  identityPush: 'identity_push',
  menuPush: 'menu_push',
  menuPull: 'menu_pull',
  ordersIngress: 'orders_ingress',
  reservations: 'reservations',
  reviews: 'reviews',
  notifyOutbound: 'notify_outbound',
} as const;

export type ChannelCapability =
  (typeof CHANNEL_CAPABILITIES)[keyof typeof CHANNEL_CAPABILITIES];

export const ChannelCapabilitySchema = z.enum([
  CHANNEL_CAPABILITIES.bootstrapRead,
  CHANNEL_CAPABILITIES.connectOauth,
  CHANNEL_CAPABILITIES.identityPull,
  CHANNEL_CAPABILITIES.identityPush,
  CHANNEL_CAPABILITIES.menuPush,
  CHANNEL_CAPABILITIES.menuPull,
  CHANNEL_CAPABILITIES.ordersIngress,
  CHANNEL_CAPABILITIES.reservations,
  CHANNEL_CAPABILITIES.reviews,
  CHANNEL_CAPABILITIES.notifyOutbound,
]);

/** Planned capabilities per connectable provider (GBP first; delivery deferred). */
export const CHANNEL_PROVIDER_CAPABILITIES: Record<
  IntegrationProviderKey,
  readonly ChannelCapability[]
> = {
  [INTEGRATION_PROVIDER_KEYS.googleBusiness]: [
    CHANNEL_CAPABILITIES.connectOauth,
    CHANNEL_CAPABILITIES.identityPull,
    CHANNEL_CAPABILITIES.identityPush,
    CHANNEL_CAPABILITIES.menuPush,
    CHANNEL_CAPABILITIES.menuPull,
    CHANNEL_CAPABILITIES.reviews,
  ],
  [INTEGRATION_PROVIDER_KEYS.uberEats]: [
    CHANNEL_CAPABILITIES.menuPush,
    CHANNEL_CAPABILITIES.ordersIngress,
  ],
  [INTEGRATION_PROVIDER_KEYS.lieferando]: [
    CHANNEL_CAPABILITIES.menuPush,
    CHANNEL_CAPABILITIES.ordersIngress,
  ],
};

export function channelHasCapability(
  provider: IntegrationProviderKey,
  capability: ChannelCapability,
): boolean {
  return CHANNEL_PROVIDER_CAPABILITIES[provider].includes(capability);
}

/** Channel Fabric processing lanes. */
export const CHANNEL_LANES = {
  presence: 'presence',
  catalog: 'catalog',
  commerce: 'commerce',
  automation: 'automation',
} as const;

export type ChannelLane = (typeof CHANNEL_LANES)[keyof typeof CHANNEL_LANES];

export const ChannelLaneSchema = z.enum([
  CHANNEL_LANES.presence,
  CHANNEL_LANES.catalog,
  CHANNEL_LANES.commerce,
  CHANNEL_LANES.automation,
]);

/** Default Presence field policies after GBP connect. */
export const DEFAULT_GBP_SYNC_POLICIES: Record<string, string> = {
  nap_hours: 'push',
  menu: 'push',
  reviews: 'notify_pull',
  legal: 'ignore',
};

export const INTEGRATION_CONNECTION_STATUS = {
  active: 'active',
  paused: 'paused',
} as const;

export const IntegrationConnectionStatusSchema = z.enum([
  INTEGRATION_CONNECTION_STATUS.active,
  INTEGRATION_CONNECTION_STATUS.paused,
]);

export const OUTBOX_ENTITY_TYPES = {
  menuCatalog: 'menu_catalog',
  personaBranding: 'persona_branding',
  leadSubmission: 'lead_submission',
} as const;

export const OutboxEntityTypeSchema = z.enum([
  OUTBOX_ENTITY_TYPES.menuCatalog,
  OUTBOX_ENTITY_TYPES.personaBranding,
  OUTBOX_ENTITY_TYPES.leadSubmission,
]);

export function outboxLaneForEntityType(
  entityType: (typeof OUTBOX_ENTITY_TYPES)[keyof typeof OUTBOX_ENTITY_TYPES],
): ChannelLane {
  if (entityType === OUTBOX_ENTITY_TYPES.leadSubmission) {
    return CHANNEL_LANES.automation;
  }
  if (entityType === OUTBOX_ENTITY_TYPES.menuCatalog) {
    return CHANNEL_LANES.catalog;
  }
  return CHANNEL_LANES.presence;
}

export const OUTBOX_OPERATIONS = {
  upsert: 'upsert',
  delete: 'delete',
} as const;

export const OutboxOperationSchema = z.enum([OUTBOX_OPERATIONS.upsert, OUTBOX_OPERATIONS.delete]);

export const OUTBOX_STATUS = {
  pending: 'pending',
  processed: 'processed',
  failed: 'failed',
} as const;

export const OutboxStatusSchema = z.enum([
  OUTBOX_STATUS.pending,
  OUTBOX_STATUS.processed,
  OUTBOX_STATUS.failed,
]);

export interface IntegrationConnectionView {
  id: string;
  provider: IntegrationProviderKey;
  credentialsRef: string | null;
  status: (typeof INTEGRATION_CONNECTION_STATUS)[keyof typeof INTEGRATION_CONNECTION_STATUS];
}

export interface IntegrationOutboxEntryView {
  id: string;
  entityType: (typeof OUTBOX_ENTITY_TYPES)[keyof typeof OUTBOX_ENTITY_TYPES];
  entityId: string;
  operation: (typeof OUTBOX_OPERATIONS)[keyof typeof OUTBOX_OPERATIONS];
  status: (typeof OUTBOX_STATUS)[keyof typeof OUTBOX_STATUS];
  lastError: string | null;
  createdAt: string;
  processedAt: string | null;
}

export const IntegrationConnectionPatchSchema = z.object({
  status: IntegrationConnectionStatusSchema,
  credentialsRef: z.string().nullable().optional(),
});

/** Channel binding id kinds — used to route Pub/Sub → project. */
export const CHANNEL_BINDING_ID_KINDS = {
  locationName: 'location_name',
  accountName: 'account_name',
  placeId: 'place_id',
} as const;

export type ChannelBindingIdKind =
  (typeof CHANNEL_BINDING_ID_KINDS)[keyof typeof CHANNEL_BINDING_ID_KINDS];

export const INTEGRATION_INBOX_STATUS = {
  pendingReview: 'pending_review',
  pulled: 'pulled',
  dismissed: 'dismissed',
} as const;

export const IntegrationInboxStatusSchema = z.enum([
  INTEGRATION_INBOX_STATUS.pendingReview,
  INTEGRATION_INBOX_STATUS.pulled,
  INTEGRATION_INBOX_STATUS.dismissed,
]);

export type IntegrationInboxStatus =
  (typeof INTEGRATION_INBOX_STATUS)[keyof typeof INTEGRATION_INBOX_STATUS];

/** Common GBP Notifications API types we surface for notify-to-pull. */
export const GBP_INBOX_EVENT_TYPES = {
  googleUpdate: 'GOOGLE_UPDATE',
  newReview: 'NEW_REVIEW',
  updatedReview: 'UPDATED_REVIEW',
  updatedLocationState: 'UPDATED_LOCATION_STATE',
  unknown: 'UNKNOWN',
} as const;

export interface IntegrationInboxItemView {
  id: string;
  provider: IntegrationProviderKey;
  eventType: string;
  externalRef: string | null;
  status: IntegrationInboxStatus;
  createdAt: string;
  summary: string;
}

export interface IntegrationSyncStatusView {
  connections: IntegrationConnectionView[];
  pendingCount: number;
  lastProcessedAt: string | null;
  /** Inbound GBP (etc.) changes awaiting owner pull decision. */
  inboxPending: IntegrationInboxItemView[];
}

/**
 * Channel sync lane (menu / persona) vs lead forward lane.
 * Lead forward must not be blocked when GBP is paused (Channel Fabric P0).
 */
export function isChannelSyncOutboxEntity(
  entityType: IntegrationOutboxEntryView['entityType'],
): boolean {
  return (
    entityType === OUTBOX_ENTITY_TYPES.menuCatalog ||
    entityType === OUTBOX_ENTITY_TYPES.personaBranding
  );
}

export function isGbpChannelPaused(connection: IntegrationConnectionView | null): boolean {
  return connection?.status === INTEGRATION_CONNECTION_STATUS.paused;
}

export function summarizeInboxEventType(eventType: string): string {
  switch (eventType) {
    case GBP_INBOX_EVENT_TYPES.googleUpdate:
      return 'Google suggested updates for your listing';
    case GBP_INBOX_EVENT_TYPES.newReview:
      return 'New Google review';
    case GBP_INBOX_EVENT_TYPES.updatedReview:
      return 'Updated Google review';
    case GBP_INBOX_EVENT_TYPES.updatedLocationState:
      return 'Listing state changed on Google';
    default:
      return 'Google Business Profile change';
  }
}
